import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { formatPhoneForDisplay } from "../../lib/phone";
import { formatISK } from "../../lib/currency";

export default function AdminBookingDetail() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState("");

  useEffect(() => {
    if (!bookingId) return;
    Promise.all([api.get(`/admin/bookings/${bookingId}`), api.get('/admin/drivers')])
      .then(([bookingRes, driversRes]) => {
        setBooking(bookingRes.data);
        setSelectedDriver(bookingRes.data.driver_id || "");
        setDrivers(driversRes.data || []);
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [bookingId]);

  if (bookingId && booking === null) return null;
  if (!booking) return <Navigate to="/admin/bookings" replace />;

  const assignDriver = async () => {
    try {
      if (booking.driver_job_id) {
        await api.patch(`/admin/driver-jobs/${booking.driver_job_id}`, { assigned_driver_id: selectedDriver || null, assigned_vehicle_id: booking.vehicle_id || null, note: 'Assigned from booking detail' });
      } else {
        await api.put(`/admin/bookings/${bookingId}`, { driver_id: selectedDriver || null });
      }
      const { data } = await api.get(`/admin/bookings/${bookingId}`);
      setBooking(data);
      toast.success('Driver assignment updated');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-booking-detail-page">
      <Link to="/admin/bookings" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900"><ArrowLeft size={14} /> Back to bookings</Link>
      <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-5">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-700">{booking.confirmation_number || booking.id}</p>
          <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight">{booking.customer_name}</h1>
          <p className="mt-1 text-sm text-slate-500 capitalize">{booking.service_type} · {booking.status}</p>
          {booking.driver_unassigned && <p data-testid="booking-detail-driver-unassigned" className="mt-3 inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900 ring-1 ring-inset ring-amber-200">Driver unassigned</p>}
        </div>
        <DetailRow label="Email" value={booking.email} />
        <DetailRow label="Phone" value={booking.phone ? formatPhoneForDisplay(booking.phone) : "—"} />
        <DetailRow label="Start" value={new Date(booking.start_at).toLocaleString()} />
        <DetailRow label="End" value={new Date(booking.end_at).toLocaleString()} />
        <DetailRow label="Vehicle" value={booking.vehicle_name || booking.vehicle_id || "—"} />
        <DetailRow label="Driver" value={booking.driver_name || booking.driver_id || "—"} />
        {(booking.service_type === 'transport' || booking.service_type === 'tours') && (
          <div className="grid gap-3 sm:grid-cols-[180px_1fr_auto] text-sm items-end">
            <p className="font-semibold text-slate-500">Assign driver</p>
            <select value={selectedDriver} onChange={(e) => setSelectedDriver(e.target.value)} data-testid="booking-detail-driver-select" className="h-10 rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900">
              <option value="">Unassigned</option>
              {drivers.map((driver) => <option key={driver.id} value={driver.id}>{driver.name}</option>)}
            </select>
            <button type="button" onClick={assignDriver} data-testid="booking-detail-driver-assign" className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800">Save</button>
          </div>
        )}
        <DetailRow label="Payment status" value={booking.payment_status || "—"} />
        <DetailRow label="Invoice mode" value={booking.invoice_mode || "—"} />
        <DetailRow label="Pricing mode" value={booking.pricing_mode || "—"} />
        <DetailRow label="Fixed price" value={booking.fixed_price_isk ? formatISK(booking.fixed_price_isk) : "—"} />
        <DetailRow label="Quote required" value={booking.quote_required ? "Yes" : "No"} />
        <DetailRow label="Route" value={booking.pickup || booking.pickup_location_name_snapshot ? `${booking.pickup || booking.pickup_location_name_snapshot || "—"} → ${booking.dropoff || booking.dropoff_location_name_snapshot || "—"}` : "—"} />
        <DetailRow label="Pickup address" value={booking.pickup_address || booking.pickup_custom_address || "—"} />
        <DetailRow label="Drop-off address" value={booking.dropoff_address || booking.dropoff_custom_address || "—"} />
        <DetailRow label="Tour" value={booking.tour_name || "—"} />
        <DetailRow label="Notes" value={booking.notes || booking.internal_notes || "—"} />
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[180px_1fr] text-sm">
      <p className="font-semibold text-slate-500">{label}</p>
      <p className="text-slate-900 break-words">{value}</p>
    </div>
  );
}